import { useTranslation } from 'react-i18next';

import { setConsent, useConsent } from '../lib/consent';
import { LocaleLink } from './navigation/LocaleLink';

/**
 * Cookie consent banner — the gate in front of GA4 (§19), and the reason `useAnalytics` loads
 * nothing until a visitor answers.
 *
 * ── Deliberately NOT a modal ─────────────────────────────────────────────────────────────────
 * No focus trap, no backdrop, no scroll lock. A consent banner that blocks the page pressures
 * the visitor into clicking whatever dismisses it fastest, which makes the resulting "consent"
 * worthless — and it would lock out anyone who simply wants to read a page. It is a
 * complementary landmark pinned to the bottom; the site stays fully usable behind it.
 *
 * ── Accept and Decline are equals ────────────────────────────────────────────────────────────
 * Same size, same weight, side by side, both one click. GDPR requires refusing to be as easy as
 * accepting, so there is no styling a "Decline" into a faint text link here — and no implied
 * consent from scrolling or from dismissing the banner. Not answering leaves it undecided,
 * which behaves exactly like a refusal.
 */
export function CookieBanner() {
  const { t } = useTranslation();
  const consent = useConsent();

  // A recorded decision — either way — retires the banner. It returns only via the footer link.
  if (consent !== null) return null;

  return (
    <div
      // `region` + label so screen-reader users can find it deliberately; `aria-live` would be
      // wrong, since it is present from first paint rather than announcing a change.
      role="region"
      aria-label={t('cookies.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-panel/95 p-4 shadow-soft backdrop-blur sm:p-5 dark:shadow-soft-dark"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-content-muted">
          {t('cookies.body')}{' '}
          <LocaleLink to="/cookie-policy" className="font-medium text-accent underline underline-offset-2">
            {t('cookies.policyLink')}
          </LocaleLink>
        </p>

        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => setConsent('denied')}
            className="min-h-11 flex-1 rounded-pill border border-line px-5 py-2.5 text-sm font-medium text-content transition-colors hover:bg-content/5 sm:flex-none"
          >
            {t('cookies.decline')}
          </button>
          <button
            type="button"
            onClick={() => setConsent('granted')}
            className="min-h-11 flex-1 rounded-pill bg-accent px-5 py-2.5 text-sm font-medium text-accent-contrast transition-opacity hover:opacity-90 sm:flex-none"
          >
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
